/**
 * TanStack Query hooks — Data Accuracy V2.
 * ⚠ Chaque hook lit un endpoint, aucune valeur en dur.
 *
 * D-DATA-ACCURACY-V2-CANAL (#367) — `channelType` propagé sur les 8 hooks +
 * inclus dans la queryKey de chacun (sinon cache croisé entre canaux). Absent
 * = « Tous ». La cohérence « fond entonnoir == score carte » n'est vraie que
 * si TOUS les hooks reçoivent la même valeur en même temps.
 */
import { useQuery } from '@tanstack/react-query'
import {
  fetchDaV2Funnel,
  fetchFailingVisits,
  fetchMerchHistory,
  fetchMerchProfile,
  fetchMerchsTable,
  fetchNationalBreakdown,
  fetchNationalSeries,
  type ChannelType,
} from '../lib/api'

const STALE = 60_000

export function useDaV2NationalSeries(weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'national-series', weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchNationalSeries(weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!weekMin && !!weekMax,
  })
}

export function useDaV2NationalBreakdown(weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'national-breakdown', weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchNationalBreakdown(weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!weekMin && !!weekMax,
  })
}

export function useDaV2MerchsTable(weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'merchs', weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchMerchsTable(weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!weekMin && !!weekMax,
  })
}

export function useDaV2MerchProfile(code: string | undefined, weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'merch-profile', code, weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchMerchProfile(code as string, weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!code,
  })
}

export function useDaV2MerchHistory(code: string | undefined, weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'merch-history', code, weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchMerchHistory(code as string, weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!code,
  })
}

/**
 * D-DATA-ACCURACY-V2-P3-ENTONNOIR (#366) — deux entonnoirs (Preuve + Qualité)
 * en RELEVÉS. `merch` optionnel : si fourni, restreint aux relevés du merch
 * (le badge import est alors désactivé — c'est un phénomène national).
 */
export function useDaV2Funnel(weekMin: string, weekMax: string, merch?: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'funnel', weekMin, weekMax, merch ?? null, channelType ?? null],
    queryFn: () => fetchDaV2Funnel(weekMin, weekMax, merch, channelType),
    staleTime: STALE,
    enabled: !!weekMin && !!weekMax,
  })
}

export function useDaV2FailingVisits(merch: string | undefined, weekMin: string, weekMax: string, channelType?: ChannelType) {
  return useQuery({
    queryKey: ['da-v2', 'failing-visits', merch, weekMin, weekMax, channelType ?? null],
    queryFn: () => fetchFailingVisits(merch as string, weekMin, weekMax, channelType),
    staleTime: STALE,
    enabled: !!merch,
  })
}
